/**
 * Displays a list of tagged preferences with source badges,
 * inline edit/remove, and an add form.
 */

import type { TaggedPreference } from '@elara/domain/models/schemas/shared-schemas';

import { useState } from 'react';

import { cn } from '@elara/client-core/lib/utils';

import { Button, Label, Textarea } from './index.js';
import { MaterialIcon } from './MaterialIcon.js';

interface PreferenceListProps {
  label: string;
  value: TaggedPreference[] | undefined;
  onChange: (prefs: TaggedPreference[]) => void;
  placeholder?: string;
  showLabel?: boolean;
}

export function PreferenceList({ label, value, onChange, placeholder, showLabel = true }: PreferenceListProps) {
  const items = value || [];
  const [newValue, setNewValue] = useState('');
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editingValue, setEditingValue] = useState('');

  const addItem = () => {
    const trimmed = newValue.trim();
    if (!trimmed) return;
    onChange([...items, { value: trimmed, source: 'user' }]);
    setNewValue('');
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const startEditing = (index: number) => {
    setEditingIndex(index);
    setEditingValue(items[index]!.value);
  };

  const saveEdit = () => {
    const trimmed = editingValue.trim();
    if (!trimmed) {
      removeItem(editingIndex);
    } else {
      const updated = [...items];
      updated[editingIndex] = { ...updated[editingIndex]!, value: trimmed };
      onChange(updated);
    }
    setEditingIndex(-1);
    setEditingValue('');
  };

  const cancelEdit = () => {
    setEditingIndex(-1);
    setEditingValue('');
  };

  return (
    <div className="space-y-3">
      <Label className={cn(!showLabel && 'sr-only')}>{label}</Label>

      {items.length > 0 && (
        <ul className="space-y-2">
          {items.map((item, index) => (
            <li
              key={index}
              className={cn(
                'flex items-start pl-3 pr-2 py-1 group rounded-sm border bg-background-secondary transition-colors',
                editingIndex === index
                  ? 'bg-background border border-accent flex-col gap-1 pb-2'
                  : 'hover:border-input gap-2',
              )}
              onClick={() => editingIndex !== index && startEditing(index)}
            >
              {editingIndex === index ? (
                <>
                  <Textarea
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.altKey || e.metaKey)) saveEdit();
                      if (e.key === 'Escape') cancelEdit();
                    }}
                    rows={1}
                    autoFocus
                    autoResize
                    className="flex-1 px-0 py-2.5 rounded-none border-0 w-full"
                  />
                  <div className="flex items-center gap-2 w-full">
                    {item.source === 'elara' && <span className="text-xs text-muted-foreground">Added by Elara</span>}
                    <div className="flex-1" />
                    <Button
                      variant="ghost"
                      size="sm"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        cancelEdit();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      className="px-2.5"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        saveEdit();
                      }}
                    >
                      <MaterialIcon icon="add" />
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <span className="flex-1 text-sm py-2.5 whitespace-pre-wrap">{item.value}</span>
                  {/* my-1 centres the 32px button on the first 40px line of text. */}
                  <div className="flex gap-1 my-1">
                    <Button
                      variant="icon"
                      size="icon-sm"
                      onClick={(e) => {
                        e.stopPropagation();

                        removeItem(index);
                      }}
                      className="h-8 w-8 hover:text-destructive"
                    >
                      <MaterialIcon icon="delete" />
                    </Button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* One row: the input grows as it wraps and the round add button stays pinned bottom-right. */}
      <div className="group/add flex items-end gap-2 pl-3 pr-2 border border-input focus-within:border-accent rounded-sm">
        <Textarea
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              addItem();
            }
          }}
          onBlur={addItem}
          rows={1}
          autoResize
          placeholder={placeholder || `Add ${label.toLowerCase()}`}
          className="flex-1 min-w-0 px-0 py-3.5 rounded-none border-0"
        />
        <Button
          size="icon-sm"
          // A plain icon while empty, filled once there's something to add.
          variant={newValue.trim() ? 'default' : 'icon'}
          className="mb-2 h-8 w-8 flex-shrink-0 rounded-full"
          onMouseDown={(e) => {
            e.preventDefault(); // Prevent textarea from losing focus
            addItem();
          }}
          disabled={!newValue.trim()}
        >
          <MaterialIcon icon="add" />
        </Button>
      </div>
    </div>
  );
}
